import { useState } from "react";
import { api } from "../lib/api";
import { setUserToken } from "../lib/userToken";
import { ErrorBox } from "./ui";
import { useT } from "../i18n";

// Candidacy-independent account verification, embedded wherever a player needs a
// token (e.g. a verified-only poll). Mirrors the candidacy company-rename proof:
// enter username → get a one-time code → rename a company to it → verify. On
// success the per-user token is stored (setUserToken) so every subsequent vote on
// this device dedups/gates on the real identity. Reuses the candidacy rename
// instruction strings so the two flows read identically.
export function VerifyAccount({ onVerified }: { onVerified?: () => void }) {
  const t = useT();
  const [username, setUsername] = useState("");
  const [nonce, setNonce] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function start() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.startUserVerify(username.trim());
      setNonce(r.nonce);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.confirmUserVerify(username.trim());
      setUserToken(r.editToken);
      setDone(true);
      onVerified?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (done) return <div className="mt-2 text-cyan text-sm font-semibold">{t("verify.success")}</div>;

  return (
    <div className="mt-2 p-3 rounded-lg border border-border bg-panel2 text-sm">
      <div className="font-semibold mb-1">{t("verify.title")}</div>
      <p className="text-muted text-xs mb-2">{t("verify.intro")}</p>
      <label className="block text-xs text-muted mb-1">{t("verify.usernameLabel")}</label>
      <input
        className="w-full bg-panel border border-border rounded px-2 py-1.5 text-sm mb-2"
        value={username}
        maxLength={40}
        onChange={(e) => setUsername(e.target.value)}
      />
      {!nonce ? (
        <button
          disabled={busy || !username.trim()}
          onClick={start}
          className="bg-accent text-white rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
        >
          {t("verify.getCode")}
        </button>
      ) : (
        <div>
          <p className="text-muted text-xs">{t("candidacy.renameStep")}</p>
          <code className="block my-1 font-mono text-cyan break-all">{nonce}</code>
          <p className="text-muted text-xs mb-2">{t("candidacy.verifyStep")}</p>
          <button
            disabled={busy}
            onClick={confirm}
            className="bg-accent text-white rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            {t("verify.verifyBtn")}
          </button>
        </div>
      )}
      {err && (
        <div className="mt-2">
          <ErrorBox msg={err} />
        </div>
      )}
    </div>
  );
}
